<template>
    <div class="col-md-6">
        <input type="hidden" name="order_id" :value="orderId">
        <div>
            <strong>Source</strong>
            <select v-model="orderSource" v-on:change="onSourceSelected" class="form-control" name="source">
                <option v-for="allowedOrderSource in allowedOrderSources">
                    {{ allowedOrderSource }}
                </option>
            </select>
            <div v-if="isPlatformOrder">
                <br>
                <div class="row">
                    <div class="col">
                        <div class="alert alert-warning">
                            <span>For platform orders a manual payment will automatically be generated. This payment will be handled the same way as normal payments triggering all normal processes.</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="orderSource === 'ORDER_CHANGE'">
            <br>
            <strong>Original order</strong>
            <order-search v-on:order-selected="onOrderSelected"
                          :showCancelledOnly="true"
                          :prefilledOrderId="preselectedOriginalOrderId"
                          :form-field-name="'original_order_uuid'">
            </order-search>
        </div>
        <br>
        <div>
            <strong>Event</strong>
            <event-search v-on:event-selected="onEventSelected"
                          :showPastEventsFilter="true"
                          :prefilledEventId="preselectedEventId">
            </event-search>
        </div>
        <br>
        <div v-if="selectedEvent">
            <strong>Ticket</strong>
            <tickets-search :partner-id="partnerId"
                            :event-uuid="selectedEvent"
                            :prefilledTicketCategoryUuid="preselectedTicketCategoryUuid"
                            v-on:ticket-selected="onTicketSelected"
            ></tickets-search>
            <br>
            <div class="row">
                <div class="col-md-3">
                    <label>
                        <strong>Adults</strong>
                        <input class="form-control" v-model="numAdults" name="num_adults" type="number">
                    </label>
                </div>
            </div>
            <div class="row" v-for="(_, index) in children">
                <div class="col-md-4">
                    <label>
                        <strong>Child {{ (index + 1) }}</strong>
                        <div class="row">
                            <div class="col">
                                <flat-pickr
                                        v-model="children[index]"
                                        class="form-control form-control-sm d-inline-block mr-1"
                                        placeholder="Select birthdate">
                                </flat-pickr>
                            </div>
                            <button type="button" class="col-md-2 btn btn-link btn-xs p-0 text-danger"
                                    v-on:click="removeChild(index)">remove
                            </button>
                        </div>
                        <input hidden name="children" v-model="children">
                    </label>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <button type="button" class="btn btn-link btn-xs p-0 text-primary" v-on:click="addChild">Add child
                    </button>
                </div>
            </div>
        </div>
        <br>
        <div v-if="canGetPrices">
            <button type="button" v-if="!loadingPricing" :disabled="loadingPricing" class="btn btn-primary"
                    v-on:click="getPricing">Get Prices
            </button>
            <div v-if="pricingError" class="alert alert-danger">{{ pricingError }}</div>
        </div>
        <br>
        <div v-if="loadingPricing">
            <i class="mdi mdi-spin mdi-loading text-gray-500"></i>
        </div>
        <div v-if="hasPrices">
            <div class="row">
                <div class="col-md-3">
                    <strong>Currency</strong>
                </div>
                <div class="col-md-3">
                    <input class="form-control" v-model="pricing.currency" disabled>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <strong>Our cost price (pp)</strong>
                </div>
                <div class="col-md-3">
                    <input class="form-control" v-model="pricing.costPricePp" disabled>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <strong>Advice selling price (pp)</strong>
                </div>
                <div class="col-md-3">
                    <input class="form-control" v-model="pricing.adviceSellingPricePp" disabled>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <strong>Selling price (pp)</strong>
                </div>
                <div class="col-md-3">
                    <div class="row">
                        <div class="col">
                            <input class="form-control" v-model="sellingPriceWhole" type="text">
                        </div>
                        <div class="col">
                            <input class="form-control" v-model="sellingPriceDecimals" type="text" maxlength="2">
                        </div>
                        <input hidden v-model="getSellingPrice" name="selling_price_pp">
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <strong>Total selling price</strong>
                </div>
                <div class="col-md-3">
                    <input class="form-control" :value="pricing.totalSellingPrice" disabled>
                </div>
            </div>
        </div>
        <br>
        <div v-if="isPlatformOrder || isEquipoOrder">
            <strong>Order details</strong>
            <div v-if="isPlatformOrder" class="row">
                <div class="col-md-6">
                    <label class="d-block">
                        <strong>Sales date</strong>
                        <flat-pickr
                                v-model="salesDate"
                                class="form-control form-control-sm d-inline-block mr-1"
                                placeholder="Select sales date"
                                name="sales_date"
                                :config="{
                                    minDate: minOrderDate,
                                }"
                        >
                        </flat-pickr>
                    </label>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6">
                    <label>
                        <strong>External order reference</strong><i v-if="isVerifyingExternalReference"
                                                                    class="mdi mdi-spin mdi-loading text-gray-500"></i>
                        <input class="form-control" v-model="externalReference" name="external_reference" type="text">
                        <small class="text-danger" v-if="externalReferenceValidationMessage">{{
                                externalReferenceValidationMessage
                            }}</small>
                    </label>
                </div>
            </div>
            <br>
            <div class="row">
                <div class="col">
                    <button type="button" class="btn btn-link btn-xs p-0 text-primary"
                            @click="addTicketNotes = !addTicketNotes">
                        Add ticket notes
                    </button>
                    <small> (Please only use when necessary. This will result in tickets not being able to be assigned
                        automatically.)</small>
                    <div v-show="addTicketNotes">
                        <textarea name="ticket_notes" class="form-control" v-model="ticketNotes"></textarea>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <manual-order-b2b
                    v-if="isB2b"
                    :companyName="companyName"
                    :email="email"
                    :travellerDataRequired="travellerDataRequired"
                    :isBlacklistedNationalityCodesAvailable="isBlacklistedNationalityCodesAvailable"
                    :onTravellerUpdated="onTravellerUpdated"
                    :isEquipoOrder="isEquipoOrder"
                    :customer="customer"
                    :emailValidationMessage="emailValidationMessage"
                    :onCountrySelected="onCountrySelected"
                    :customerReference="customerReference"
                    :prefilledTravellers="prefilledTravellers"
                    :travellerCount="travellerCount"
                    :prefilledCountryCode="prefilledCountryCode"
                    :country="country"
                    :isVerifyingEmail="isVerifyingEmail"
                    :customerInformationMessage="customerInformationMessage"
                    :fillCustomerInfo="fillCustomerInfo"
            ></manual-order-b2b>
            <manual-order-b2c
                    v-if="!isB2b"
                    :email="email"
                    :travellerDataRequired="travellerDataRequired"
                    :isBlacklistedNationalityCodesAvailable="isBlacklistedNationalityCodesAvailable"
                    :onTravellerUpdated="onTravellerUpdated"
                    :isEquipoOrder="isEquipoOrder"
                    :customer="customer"
                    :emailValidationMessage="emailValidationMessage"
                    :onCountrySelected="onCountrySelected"
                    :customerReference="customerReference"
                    :prefilledTravellers="prefilledTravellers"
                    :travellerCount="travellerCount"
                    :prefilledCountryCode="prefilledCountryCode"
                    :country="country"
                    :phoneNumber="phoneNumber"
                    :birthDate="birthDate"
                    :isBookingForSomeoneElse="isBookingForSomeoneElse"
                    :isVerifyingEmail="isVerifyingEmail"
                    :onNationalitySelected="onNationalitySelected"
                    :nationality="nationality"
                    :customerInformationMessage="customerInformationMessage"
                    :fillCustomerInfo="fillCustomerInfo"
            ></manual-order-b2c>
        </div>

        <div v-if="isPlatformOrder">
            <input id="acknowledge-payment-creation" v-model="paymentCreationAcknowledged" type="checkbox">
            <label for="acknowledge-payment-creation">I am aware this will automatically generate a payment for this
                order and will result in all normal processes being started.</label>
        </div>
        <div v-if="noAwayFansAllowed">
            <input type="checkbox" id="customer_is_no_away_fan" name="customer_is_no_away_fan"
                   v-model="customerIsNoAwayFan">
            <label for="customer_is_no_away_fan">I hereby confirm the customer is not an away fan</label>
        </div>
        <div v-if="!isPlatformOrder">
            <input v-model="sendPaymentLink"
                   id="send-payment-link"
                   type="checkbox">
            <label for="send-payment-link">Send payment link to customer</label>
            <br>
        </div>
        <div>
            <input v-model="sendOrderConfirmation"
                   id="send-order-confirmation"
                   type="checkbox">
            <label for="send-order-confirmation">Send order confirmation</label>
            <br>
        </div>
        <br>
        <div v-if="sendPaymentLink">
            <input v-model="isSetCustomExpiryTime"
                   id="is_set_custom_expiry_time"
                   name="is_set_custom_expiry_time"
                   type="checkbox">
            <label for="is_set_custom_expiry_time">Set custom payment expiry time</label>
            <br>
            <div v-if="isSetCustomExpiryTime" class="row">
                <div class="col-md-6">
                    <label class="d-block">
                        <strong>Custom Payment Expiry Time</strong> <small>(min)</small>
                        <input v-model="customExpiryTime" class="form-control" type="number" name="custom_expiry_time"
                               min="0" max="480">
                    </label>
                </div>
            </div>
        </div>
        <div v-if="canMakeOrder">
            <button class="btn btn-primary">Place order</button>
        </div>
        <input v-if="!isPlatformOrder"
               v-model="sendPaymentLink"
               name="send_payment_link"
               hidden>
        <input v-model="sendOrderConfirmation"
               name="send_order_confirmation"
               hidden>
    </div>
</template>

<script>
import Vue from "vue";
import FlatPickr from 'vue-flatpickr-component/src/component';
import CountryCodeSelector from "./CountryCodeSelector";
import BigNumber from "bignumber.js";
import OrderTravellerForm from "./OrderTravellerForm";
import qs from "qs";
import axios from "axios";
import ManualOrderB2b from "./ManualOrderB2b.vue";
import ManualOrderB2c from "./ManualOrderB2c.vue";

const initialPricingData = {
    currency: null,
    costPricePp: null,
    adviceSellingPricePp: null,
    totalSellingPrice: null
};

export default {
    name: "CreateManualOrder",
    components: {CountryCodeSelector, FlatPickr, OrderTravellerForm, ManualOrderB2b, ManualOrderB2c},

    props: {
        partnerId: {
            required: true
        },
        preselectedOriginalOrderId: {
            default: null
        },
        preselectedEventId: {
            default: null
        },
        preselectedTicketCategoryUuid: {
            default: null
        },
        firstName: {
            default: ''
        },
        lastName: {
            default: ''
        },
        email: {
            default: ''
        },
        phoneNumber: {
            default: ''
        },
        birthDate: {
            default: ''
        },
        nationality: {
            default: ''
        },
        country: {
            default: ''
        },
        city: {
            default: ''
        },
        zipCode: {
            default: ''
        },
        gender: {
            default: ''
        },
        addressLine1: {
            default: ''
        },
        addressLine2: {
            default: ''
        },
        isB2b: {
            type: Boolean,
            default: false
        },
        prefilledNumAdults: {
            default: 0
        },
        prefilledChildren: {
            default: []
        },
        prefilledOrderId: {
            default: null
        },
        prefilledSellingPrice: {
            default: null
        },
        prefilledTravellers: {
            default: []
        },
        prefilledTravellerDataRequired: {
            default: false
        },
        prefilledIsBookingForSomeoneElse: {
            default: false
        },
        prefilledSalesDate: {
            default: null
        },
        allowedOrderSources: {
            default: []
        },
        prefilledOrderSource: {
            default: null
        },
        platformOrderSources: {
            default: []
        },
        equipoOrderSource: {
            default: null
        },
        prefilledTicketNotes: {
            default: null
        },
        prefilledExternalReference: {
            default: null
        },
        prefilledCustomerReference: {
            default: null
        },
        prefilledCustomerIsNoAwayFan: {
            default: false
        },
        prefilledCountryCode: {
            default: null
        }
    },

    data: function () {
        return {
            selectedOriginalOrder: null,
            selectedEvent: null,
            restrictions: null,
            selectedTicket: null,
            loadingPricing: false,
            orderId: this.prefilledOrderId,
            sendPaymentLink: true,
            sendOrderConfirmation: true,
            sellingPriceWhole: null,
            sellingPriceDecimals: null,
            pricing: {
                currency: null,
                costPricePp: null,
                adviceSellingPricePp: null,
                totalSellingPrice: null
            },
            pricingError: null,
            numAdults: this.prefilledNumAdults,
            children: this.prefilledChildren,
            travellers: this.prefilledTravellers,
            travellerDataRequired: this.prefilledTravellerDataRequired,
            isBookingForSomeoneElse: this.prefilledIsBookingForSomeoneElse,
            customer: {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phoneNumber: this.phoneNumber,
                birthDate: this.birthDate,
                nationality: this.nationality,
                gender: this.gender,
                address: {
                    addressLine1: this.addressLine1,
                    addressLine2: this.addressLine2,
                    zipCode: this.zipCode,
                    country: this.country,
                    city: this.city,
                }
            },
            orderSource: this.prefilledOrderSource,
            paymentCreationAcknowledged: false,
            salesDate: this.prefilledSalesDate,
            addTicketNotes: false,
            ticketNotes: this.prefilledTicketNotes,
            externalReference: this.prefilledExternalReference,
            customerReference: this.prefilledCustomerReference,
            customerIsNoAwayFan: this.prefilledCustomerIsNoAwayFan,
            customerEmailVerified: false,
            emailValidationMessage: null,
            customerInformationMessage: null,
            isVerifyingEmail: false,
            externalReferenceIsValid: false,
            isVerifyingExternalReference: false,
            externalReferenceValidationMessage: null,
            companyName: null,
            fetchedCustomerInfo: null,
            isSetCustomExpiryTime: false,
            customExpiryTime: null,
            minOrderDate: new Date(new Date().setDate(new Date().getDate() - 120)).toISOString().slice(0,10)
        };
    },

    mounted() {
        if (this.customerEmail != null && this.customerEmail !== "") {
            this.asyncVerifyEmail();
        }

        if (this.isPlatformOrder && this.externalReference != null && this.externalReference !== "") {
            this.asyncVerifyExternalReferenceExists();
        }
    },

    watch: {
        sellingPriceWhole: function () {
            this.calculateTotalPrice();
        },
        sellingPriceDecimals: function () {
            this.calculateTotalPrice();
        },
        customerEmail: function () {
            this.asyncVerifyEmail();
        },
        externalReference: function () {
            if (this.isPlatformOrder) {
                this.asyncVerifyExternalReferenceExists();
            }
        }
    },

    computed: {
        externalReference() {
            return this.externalReference;
        },
        customerEmail() {
            return this.customer.email;
        },
        canGetPrices() {
            return this.orderSource &&
                    this.selectedEvent &&
                    this.selectedTicket &&
                    this.numAdults > 0 &&
                    (this.orderSource !== 'ORDER_CHANGE' || this.selectedOriginalOrder);
        },
        hasPrices() {
            return this.pricing.costPricePp && this.pricing.adviceSellingPricePp;
        },
        canMakeOrder() {
            return this.hasPrices &&
                    this.getSellingPrice &&
                    !this.loadingPricing &&
                    this.customerEmailVerified &&
                    !(this.isPlatformOrder && !this.platformOrderInfoComplete) &&
                    !(this.isPlatformOrder && !this.paymentCreationAcknowledged) &&
                    !(this.noAwayFansAllowed && !this.customerIsNoAwayFan) &&
                    !(this.isEquipoOrder && !this.equipoOrderInfoComplete);
        },
        customerInfoComplete() {
            return this.firstName &&
                    this.lastName &&
                    this.email &&
                    this.phone &&
                    this.addressLine1 &&
                    this.country &&
                    this.birthDate;
        },
        travellerCount() {
            return (parseInt(this.numAdults) + this.children?.length);
        },
        isPlatformOrder() {
            return this.platformOrderSources.includes(this.orderSource);
        },
        platformOrderInfoComplete() {
            return this.salesDate && this.externalReference.trim() && this.externalReferenceIsValid;
        },
        isEquipoOrder() {
            return this.orderSource === this.equipoOrderSource;
        },
        equipoOrderInfoComplete() {
            return this.externalReference?.trim();
        },
        noAwayFansAllowed() {
            return this.restrictions && this.restrictions.allow_away_fans === false;
        },
        isBlacklistedNationalityCodesAvailable() {
            return this.restrictions?.blacklisted_nationality_codes?.length > 0;
        },
        getSellingPrice() {
            return this.sellingPriceWhole + '.' + this.sellingPriceDecimals;
        },
        getPrefilledSellingPrice() {
            return this.prefilledSellingPrice;
        }
    },

    methods: {
        setSellingPriceFromString(sellingPrice) {
            this.sellingPriceWhole = sellingPrice.split('.')[0];
            this.sellingPriceDecimals = sellingPrice.split('.')[1] ?? '00';
        },

        calculateTotalPrice() {
            if (this.hasPrices) {
                this.pricing.totalSellingPrice = (new BigNumber(this.getSellingPrice)).multipliedBy(this.travellerCount).toFormat(2);
            }
        },

        asyncVerifyExternalReferenceExists() {
            this.isVerifyingExternalReference = true;
            this.verifyExternalReference();
        },

        verifyExternalReference: _.debounce(function () {
            axios.get(`/admin/api/orders?filters[external_reference]=` + this.externalReference + '&filters[source]=' + this.orderSource)
                    .then((resp) => {
                        this.isVerifyingExternalReference = false;
                        let orders = resp.data.data;

                        if (orders.length > 0) {
                            this.externalReferenceIsValid = false;
                            this.externalReferenceValidationMessage = 'This source <-> external reference combination already exists.'
                            return;
                        }

                        this.externalReferenceIsValid = true;
                        this.externalReferenceValidationMessage = null;
                    })
                    .catch((err) => {
                        this.isVerifyingExternalReference = false;
                        this.externalReferenceValidationMessage = 'Something went wrong when verifying this external reference';
                    });
        }, 500),

        asyncVerifyEmail() {
            this.isVerifyingEmail = true;
            this.verifyEmail();
        },

        verifyEmail: _.debounce(function () {
            axios.post(`/admin/api/verify-email`, {'email': this.customer.email})
                    .then((resp) => {
                        this.isVerifyingEmail = false;
                        this.customerInformationMessage = null;
                        this.customerEmailVerified = resp.data.success;

                        if (this.customerEmailVerified) {
                            this.emailValidationMessage = null;
                            this.getExistingCustomer();
                        }

                        if (!this.customerEmailVerified) {
                            this.emailValidationMessage = 'Please provide a valid email';
                        }

                    })
                    .catch((err) => {
                        this.isVerifyingEmail = false;
                        this.emailValidationMessage = 'Something went wrong when verifying this email';

                        if (err.response.status === 400) {
                            this.emailValidationMessage = 'Please provide a valid email';
                        }
                    });
        }, 500),

        getExistingCustomer: _.debounce(function () {
            const stringifiedEmail = qs.stringify({
                filters: {
                    query: this.customer.email,
                }
            });

            axios.get(`/admin/api/customers?${stringifiedEmail}`,)
                    .then((resp) => {
                        if (resp.data.data.length > 0) {
                            this.customerInformationMessage = "Do you want these details to be filled in? \n";
                            if (resp.data.data[0].order.customer.company_name) {
                                this.customerInformationMessage += resp.data.data[0].order.customer.copmany_name + "\n";
                            }
                            if (resp.data.data[0].order.customer.first_name) {
                                this.customerInformationMessage += resp.data.data[0].order.customer.first_name + "\n";
                            }
                            if (resp.data.data[0].order.customer.last_name) {
                                this.customerInformationMessage += resp.data.data[0].order.customer.last_name + "\n";
                            }
                            if (resp.data.data[0].order.customer.address_line_1) {
                                this.customerInformationMessage += resp.data.data[0].order.customer.address_line_1 + "\n";
                            }
                            if (resp.data.data[0].order.customer.city) {
                                this.customerInformationMessage += resp.data.data[0].order.customer.city + "\n";
                            }
                            this.fetchedCustomerInfo = resp.data.data[0].order.customer
                        }
                    })
                    .catch();
        }, 500),

        async fillCustomerInfo() {
            this.customer.address.addressLine1 = this.fetchedCustomerInfo.address_line_1;
            this.customer.address.addressLine2 = this.fetchedCustomerInfo.address_line_2;
            this.customer.address.zipCode = this.fetchedCustomerInfo.zip_code;
            this.customer.address.city = this.fetchedCustomerInfo.city;
            this.customer.address.country = this.fetchedCustomerInfo.country;
            this.customer.firstName = this.fetchedCustomerInfo.first_name;
            this.customer.lastName = this.fetchedCustomerInfo.last_name;
            this.customer.phoneNumber = this.fetchedCustomerInfo.phone_number;
            this.customer.nationality = this.fetchedCustomerInfo.nationality;
            this.customer.gender = this.fetchedCustomerInfo.gender;
            this.companyName = this.fetchedCustomerInfo.company_name;
        },

        async getPricing() {
            this.loadingPricing = true;
            this.pricingError = null;

            if (this.orderId === null || this.orderId === '') {
                try {
                    await this.createOrder();
                } catch (e) {
                    this.pricingError = e;
                    this.loadingPricing = false;
                    return;
                }
            } else {
                try {
                    await this.updateOrder();
                } catch (e) {
                    this.pricingError = e;
                    this.loadingPricing = false;
                    return;
                }
            }

            return axios.get(`/admin/api/orders/${this.orderId}/pricing?ticket_category_uuid=${this.selectedTicket}`)
                    .then((resp) => {
                        this.pricing = resp.data;
                        this.setSellingPriceFromString(this.pricing.adviceSellingPricePp);

                        if (this.prefilledSellingPrice != null && this.prefilledSellingPrice !== '') {
                            this.setSellingPriceFromString(this.getPrefilledSellingPrice);
                            this.prefilledSellingPrice = null;
                        }

                        this.calculateTotalPrice();
                        this.loadingPricing = false;
                    }).catch((err) => {
                        const errorMessage = err?.response?.data?.message;
                        this.pricingError = errorMessage || 'Could not get prices. Unknown error occurred.';
                        this.loadingPricing = false;
                    });
        },

        updateOrder() {
            return axios.put(`/admin/api/orders/${this.orderId}`,
                    {
                        'num_adults': this.numAdults,
                        'children': this.children,
                    })
                    .then((resp) => {
                        this.orderId = resp.data.id;
                    });

        },

        createOrder() {
            return axios.post('/admin/api/orders/create',
                    {
                        'partner_id': this.partnerId,
                        'event_uuid': this.selectedEvent,
                        'ticket_category_uuid': this.selectedTicket,
                        'num_adults': this.numAdults,
                        'children': this.children,
                        'source': this.orderSource,
                        'original_order_uuid': this.selectedOriginalOrder
                    })
                    .catch((err) => {
                        throw err.response.data.message;
                    })
                    .then((resp) => {
                        this.orderId = resp.data.id;
                    });
        },

        onOrderSelected(uuid) {
            this.reset();
            this.selectedOriginalOrder = uuid;
        },

        onEventSelected(uuid, travellerDataRequired, restrictions) {
            this.reset();
            this.selectedEvent = uuid;
            this.travellerDataRequired = travellerDataRequired;
            this.restrictions = restrictions;
        },

        onTicketSelected(uuid) {
            this.selectedTicket = uuid;

            if (this.orderId !== null && this.orderId !== '') {
                this.getPricing();
            }
        },

        onCountrySelected(value) {
            this.customer.address.country = value;
        },

        onNationalitySelected(value) {
            this.customer.nationality = value;
        },

        onTravellerUpdated(value) {
            this.travellers[value.travellerIndex] = {
                firstName: value.firstName,
                lastName: value.lastName,
                nationality: value.nationality
            }
        },

        onSourceSelected(value) {
            this.reset();
        },

        removeChild(index) {
            return Vue.delete(this.children, index)
        },

        addChild() {
            return this.children.push('');
        },

        resetPricing() {
            this.pricing = {...this.pricing, ...initialPricingData};
        },

        reset() {
            this.resetPricing();
            this.sellingPriceWhole = null;
            this.sellingPriceDecimals = null;
            this.orderId = null;
            this.restrictions = null;
        }
    }
}
</script>
